<template>
  <div class="user-manager space-y-6 animate-[fadeIn_0.6s_ease-out]">
    <!-- 页面标题 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F5D76E] to-[#F2C94C] flex items-center justify-center shadow-sm">
          <svg class="w-5 h-5 text-[#1B3C59]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-black text-slate-900">用户列表</h3>
        </div>
      </div>
    </div>

    <!-- 前台关联提示 -->
    <div class="bg-blue-50/80 backdrop-blur-sm border border-blue-100/60 rounded-2xl p-4 flex items-start gap-3 text-sm text-blue-700">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p class="font-medium mb-1">前台关联</p>
        <p class="text-blue-600">用户数据来自前台用户的学习行为记录，包括学习进度、收藏、点赞等交互数据</p>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 flex items-center gap-4 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
        <div class="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <svg class="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </div>
        <div>
          <div class="text-2xl font-bold text-slate-900">{{ userList.length }}</div>
          <div class="text-sm text-slate-400 mt-0.5">活跃用户</div>
        </div>
      </div>

      <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 flex items-center gap-4 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
        <div class="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <svg class="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <div class="text-2xl font-bold text-slate-900">{{ totalStudyRecords }}</div>
          <div class="text-sm text-slate-400 mt-0.5">学习记录</div>
        </div>
      </div>

      <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 flex items-center gap-4 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
        <div class="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
          <svg class="w-6 h-6 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div>
          <div class="text-2xl font-bold text-slate-900">{{ totalPracticeRecords }}</div>
          <div class="text-sm text-slate-400 mt-0.5">演练记录</div>
        </div>
      </div>
    </div>

    <!-- 用户列表 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <el-table :data="userList" stripe size="small" style="width: 100%">
        <el-table-column prop="id" label="用户ID" width="120" />
        <el-table-column prop="role" label="角色" width="100">
          <template #default="{ row }">
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 text-xs rounded font-medium',
                row.role === '管理员' ? 'bg-purple-50 text-purple-600' :
                row.role === '运营人员' ? 'bg-amber-50 text-amber-600' :
                'bg-slate-50 text-slate-500'
              ]"
            >
              {{ row.role }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="studyCount" label="学习记录" width="100" align="center" />
        <el-table-column prop="practiceCount" label="演练记录" width="100" align="center" />
        <el-table-column prop="bookmarks" label="收藏" width="80" align="center" />
        <el-table-column prop="likes" label="点赞" width="80" align="center" />
        <el-table-column prop="lastActive" label="最后活跃" width="160" />
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="{ row }">
            <button @click="viewDetail(row)" class="text-[#F2C94C] hover:text-[#E5B73B] text-sm font-medium transition-colors">查看</button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { PERMISSIONS } from '../../data/adminConfig'

export default {
  name: 'UserManager',

  computed: {
    ...mapState(['userStats', 'studyRecords', 'practiceRecords', 'userRole']),
    ...mapGetters(['hasPermission']),

    PERMISSIONS() {
      return PERMISSIONS
    },

    userList() {
      // V1: 从当前用户数据 + 学习/演练记录生成活跃用户统计
      const users = []

      // 当前登录用户
      users.push({
        id: 'current-user',
        role: this.getRoleLabel(this.userRole),
        studyCount: this.studyRecords?.length || 0,
        practiceCount: this.practiceRecords?.length || 0,
        bookmarks: this.userStats?.bookmarks?.length || 0,
        likes: this.userStats?.likes?.length || 0,
        lastActive: '刚刚',
      })

      // 模拟一些用户数据
      const mockUsers = [
        { id: 'user-001', role: '普通用户', studyCount: 12, practiceCount: 8, bookmarks: 3, likes: 5, lastActive: '2小时前' },
        { id: 'user-002', role: '普通用户', studyCount: 5, practiceCount: 3, bookmarks: 1, likes: 2, lastActive: '1天前' },
        { id: 'user-003', role: '普通用户', studyCount: 23, practiceCount: 15, bookmarks: 8, likes: 12, lastActive: '3小时前' },
        { id: 'user-004', role: '普通用户', studyCount: 0, practiceCount: 2, bookmarks: 0, likes: 1, lastActive: '5天前' },
        { id: 'user-005', role: '运营人员', studyCount: 8, practiceCount: 6, bookmarks: 2, likes: 4, lastActive: '1小时前' },
      ]

      return [...users, ...mockUsers]
    },

    totalStudyRecords() {
      return this.studyRecords?.length || 0
    },

    totalPracticeRecords() {
      return this.practiceRecords?.length || 0
    },
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    getRoleLabel(role) {
      const map = {
        'user': '普通用户',
        'operator': '运营人员',
        'admin': '管理员',
      }
      return map[role] || role
    },

    viewDetail(row) {
      this.$message.info(`查看用户 ${row.id} 的详细数据（功能开发中）`)
    },
  },
}
</script>
